import { Box, Button, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { CardHeader, CardHeading, CardSubline, GroupLabel, Mark, Note, RowsFrame, SaveFailed, SettingsCard } from './cardParts';
import { bandKey } from './criteriaDiff';
import {
  addCondition,
  addReviewRule,
  canAddCondition,
  canRemoveCondition,
  conditionErrors,
  draftHasErrors,
  isAlternate,
  measureChoices,
  removeCondition,
  setConditionMeasure,
  setConditionValue,
  type DraftBand,
  type DraftCondition,
  type DraftRule,
} from './criteriaEdit';
import { bandLabel, formatMinimum } from './criteriaView';
import type { Condition, GradeBand, Outcome } from './settingsApi';
import { SETTINGS } from './tokens';

export const FIX_VALUES_FIRST = 'Fix the highlighted values first';

type Props = {
  stateName: string;
  draft: DraftBand[];
  record: GradeBand[];
  busy: boolean;
  saveFailed: boolean;
  failure: string | null;
  onChange: (draft: DraftBand[]) => void;
  onCancel: () => void;
  onReset: () => void;
  onSave: () => void;
};

const outcomeLabel = (outcome: Outcome) => (outcome === 'exits' ? 'Exits' : 'Eligible for review');

function recordConditionOf(record: Map<string, GradeBand>, key: string, outcome: Outcome, measure: string): { rule: boolean; condition?: Condition } {
  const rule = record.get(key)?.ruleSets.find((r) => r.outcome === outcome);
  return { rule: rule !== undefined, condition: rule?.conditions.find((c) => c.measure === measure) };
}

function conditionMark(stateName: string, error: string | null, alternate: boolean, found: { rule: boolean; condition?: Condition }, value: string, id: string) {
  if (error) {
    return (
      <Typography component="span" id={id} sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.error }}>
        {error}
      </Typography>
    );
  }
  if (!found.condition) return <Mark>{`Added · not in ${stateName}’s rule`}</Mark>;
  if (Number(value) !== found.condition.minimum) return <Mark>{`Changed from ${stateName}’s ${formatMinimum(found.condition.minimum, alternate)}`}</Mark>;
  return null;
}

function ConditionFields({
  stateName,
  band,
  rule,
  condition,
  index,
  id,
  found,
  error,
  removable,
  locked,
  onMeasure,
  onValue,
  onRemove,
}: {
  stateName: string;
  band: DraftBand;
  rule: DraftRule;
  condition: DraftCondition;
  index: number;
  id: string;
  found: { rule: boolean; condition?: Condition };
  error: string | null;
  removable: boolean;
  locked: boolean;
  onMeasure: (measure: string) => void;
  onValue: (value: string) => void;
  onRemove: () => void;
}) {
  const alternate = isAlternate(band);
  const changed = !error && (!found.condition || Number(condition.value) !== found.condition.minimum);
  const label = `${outcomeLabel(rule.outcome)}, condition ${index + 1}`;
  const errorId = `${id}-error`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
      <Typography
        component="span"
        sx={{ width: SETTINGS.labelWidth, flexShrink: 0, pt: '8px', fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', fontWeight: index === 0 ? 700 : 400, color: index === 0 ? SETTINGS.ink : SETTINGS.muted }}
      >
        {index === 0 ? outcomeLabel(rule.outcome) : 'and'}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          component="select"
          aria-label={`${label}: score`}
          value={condition.measure}
          disabled={locked}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onMeasure(e.target.value)}
          sx={{
            width: '100%',
            height: 40,
            px: '12px',
            borderRadius: '8px',
            border: !found.condition ? `2px solid ${SETTINGS.primary}` : `1px solid ${SETTINGS.divider}`,
            bgcolor: SETTINGS.cardBg,
            color: SETTINGS.ink,
            fontFamily: SETTINGS.font,
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          {measureChoices(alternate).map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Box>
        {conditionMark(stateName, error, alternate, found, condition.value, errorId)}
      </Box>
      <Box
        component="input"
        inputMode="decimal"
        aria-label={`${label}: at or above`}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        value={condition.value}
        disabled={locked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValue(e.target.value)}
        sx={{
          width: 72,
          height: 40,
          flexShrink: 0,
          borderRadius: '8px',
          border: error ? `2px solid ${SETTINGS.error}` : changed ? `2px solid ${SETTINGS.primary}` : `1px solid ${SETTINGS.divider}`,
          bgcolor: SETTINGS.cardBg,
          color: SETTINGS.ink,
          fontFamily: SETTINGS.font,
          fontSize: 16,
          fontWeight: 700,
          textAlign: 'center',
        }}
      />
      <IconButton aria-label="Remove this condition" onClick={onRemove} disabled={locked || !removable} sx={{ width: 40, height: 40, borderRadius: '8px', color: SETTINGS.muted }}>
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
}

export function ExitCriteriaEditor({ stateName, draft, record, busy, saveFailed, failure, onChange, onCancel, onReset, onSave }: Props) {
  const recordByKey = new Map(record.map((b) => [bandKey(b), b]));
  const errors = draftHasErrors(draft);
  return (
    <SettingsCard labelledBy="exit-criteria-title">
      <CardHeader>
        <CardHeading id="exit-criteria-title">Edit your district’s exit criteria</CardHeading>
        <CardSubline>{`Starting from the ${stateName} state default. Change only what’s different in your district.`}</CardSubline>
      </CardHeader>
      <RowsFrame>
        <Box sx={{ display: 'flex', gap: '16px', p: '12px 16px', bgcolor: SETTINGS.noteBg, borderBottom: `1px solid ${SETTINGS.divider}`, fontFamily: SETTINGS.font, fontSize: 12, lineHeight: '16px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: SETTINGS.muted }}>
          <Box component="span" sx={{ width: SETTINGS.labelWidth, flexShrink: 0 }}>
            Outcome
          </Box>
          <Box component="span" sx={{ flex: 1 }}>
            Score
          </Box>
          <Box component="span" sx={{ width: 112, flexShrink: 0 }}>
            At or above
          </Box>
        </Box>
        {draft.map((band, b) => {
          const shape: GradeBand = { assessment: band.assessment, grades: band.grades, noExit: band.noExit, ruleSets: [] };
          const key = bandKey(shape);
          const label = bandLabel(shape);
          return (
            <Box key={key} data-testid="criteria-edit-band" data-label={label} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pb: '16px', '& + &': { borderTop: `1px solid ${SETTINGS.divider}`, pt: '4px' } }}>
              <GroupLabel>{label}</GroupLabel>
              {band.noExit && (
                <Typography sx={{ px: '16px', fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.muted }}>
                  Students can’t exit on this assessment.
                </Typography>
              )}
              {band.rules.map((rule, r) => (
                <Box
                  key={rule.outcome}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    p: '8px 16px',
                    ...(rule.outcome === 'eligible_for_review' ? { mx: '12px', px: '12px', borderRadius: '8px', bgcolor: SETTINGS.tint } : {}),
                  }}
                >
                  {rule.conditions.map((condition, c) => (
                    <ConditionFields
                      key={`${rule.outcome}-${c}`}
                      stateName={stateName}
                      band={band}
                      rule={rule}
                      condition={condition}
                      index={c}
                      id={`criteria-${b}-${r}-${c}`}
                      found={recordConditionOf(recordByKey, key, rule.outcome, condition.measure)}
                      error={conditionErrors(draft, b, r)[c] ?? null}
                      removable={canRemoveCondition(draft, b, r, c)}
                      locked={busy || band.noExit}
                      onMeasure={(measure) => onChange(setConditionMeasure(draft, b, r, c, measure))}
                      onValue={(value) => onChange(setConditionValue(draft, b, r, c, value))}
                      onRemove={() => onChange(removeCondition(draft, b, r, c))}
                    />
                  ))}
                  {canAddCondition(draft, b, r) && (
                    // Lines the button's icon up with the score fields, past the 16px gap after the label column.
                    <Box sx={{ pl: `${SETTINGS.labelWidth + 8}px` }}>
                      <Button variant="text" startIcon={<AddIcon />} disabled={busy} onClick={() => onChange(addCondition(draft, b, r))}>
                        Add condition
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
              {!band.noExit && !band.rules.some((rule) => rule.outcome === 'eligible_for_review') && (
                <Box sx={{ px: '16px' }}>
                  <Button variant="text" startIcon={<AddIcon />} disabled={busy} onClick={() => onChange(addReviewRule(draft, b))}>
                    Add a review rule
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </RowsFrame>
      <Note>{`Saving applies to your district right away. Because this differs from our ${stateName} record, our team will check it. If ${stateName}’s rule has changed, we’ll correct it for every district in the state.`}</Note>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pt: '16px', borderTop: `1px solid ${SETTINGS.divider}` }}>
        <Button variant="text" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <SaveFailed visible={saveFailed} />
        {failure && (
          <Typography role="alert" sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.error }}>
            {failure}
          </Typography>
        )}
        <Button variant="outlined" onClick={onReset} disabled={busy}>
          {`Reset to ${stateName} default`}
        </Button>
        <Box component="span" title={errors ? FIX_VALUES_FIRST : undefined}>
          <Button variant="contained" onClick={onSave} disabled={busy || errors}>
            Save criteria
          </Button>
        </Box>
      </Box>
    </SettingsCard>
  );
}
